import { useEffect, useState } from 'react';
import { Dropdown, Form } from 'react-bootstrap';
import type { DropdownSelectOption } from '../../types/types';

interface DropdownSelectProps {
  options: DropdownSelectOption[];
  selectedIds?: string | number | string[];
  onValueChanged: (
    selected: DropdownSelectOption | DropdownSelectOption[]
  ) => void;
  placeholder?: string;
  isInvalid?: boolean;
  multiselect?: boolean;
  full?: boolean;
  disabled?: boolean;
  hideSearch?: boolean; // NEW PROP
}

export default function DropdownSelect({
  options,
  selectedIds,
  onValueChanged,
  placeholder = 'Select...',
  isInvalid = false,
  multiselect = false,
  full = false,
  disabled = false,
  hideSearch = false, // default: show search
}: DropdownSelectProps) {
  const [filter, setFilter] = useState('');
  const [show, setShow] = useState(false);

  const [selected, setSelected] = useState<DropdownSelectOption | null>(null);
  const [selectedList, setSelectedList] = useState<DropdownSelectOption[]>([]);

  // Initialize selection
  useEffect(() => {
    if (!multiselect && selectedIds !== undefined) {
      const found = options.find((o) => o.id === selectedIds);
      if (found) setSelected(found);
    } else if (multiselect && selectedIds !== undefined) {
      const found = options.filter((o) =>
        (selectedIds as string[]).includes(o.id.toString())
      );
      if (found) setSelectedList(found);
    }
  }, [options, selectedIds, multiselect]);

  const filteredOptions = options?.filter((o) =>
    o.label.toLowerCase().includes(filter.toLowerCase())
  );

  const handleSingleSelect = (id: string | number) => {
    const option = options.find((o) => o.id === id);
    if (option) {
      setSelected(option);
      onValueChanged(option);
    }
    setShow(false);
    setFilter('');
  };

  const handleMultiToggle = (id: string | number) => {
    const option = options.find((o) => o.id === id);
    if (!option) return;

    let newSelection: DropdownSelectOption[];
    if (selectedList.some((s) => s.id === id)) {
      newSelection = selectedList.filter((s) => s.id !== id);
    } else {
      newSelection = [...selectedList, option];
    }
    setSelectedList(newSelection);
    onValueChanged(newSelection);
  };

  return (
    <Dropdown
      show={show}
      onToggle={(isOpen) => {
        if (!disabled) setShow(isOpen);
      }}
      className="w-100"
    >
      <Dropdown.Toggle
        disabled={disabled}
        as="div"
        className={`w-100 d-flex justify-content-between align-items-center border rounded px-3 py-2 ${
          isInvalid ? 'border border-danger' : ''
        }`}
        style={{ cursor: 'pointer' }}
      >
        <span
          className="text-truncate"
          style={{
            maxWidth: '100%',
            display: 'inline-block',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {multiselect
            ? selectedList.length > 0
              ? `${selectedList.length} Selected`
              : placeholder
            : selected
            ? (selected as DropdownSelectOption).label
            : placeholder}
        </span>
      </Dropdown.Toggle>

      <Dropdown.Menu
        style={{
          maxHeight: '300px',
          overflowY: 'auto',
          maxWidth: '380px',
          ...(full && { width: '100%', maxWidth: undefined }),
        }}
        // Prevent closing when clicking inside (important for multiselect)
        onClick={(e) => {
          if (multiselect) e.stopPropagation();
        }}
      >
        {/* Optional Search */}
        {!hideSearch && (
          <div className="px-3 py-2">
            <Form.Control
              autoFocus
              type="text"
              placeholder="Filter..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
        )}

        {filteredOptions.length > 0 ? (
          filteredOptions.map((o) => (
            <Dropdown.Item
              key={o.id}
              as="div" // Prevent internal click closing dropdown
              onClick={(e) => {
                e.stopPropagation();
                if (multiselect) {
                  handleMultiToggle(o.id);
                } else {
                  handleSingleSelect(o.id);
                }
              }}
            >
              {multiselect ? (
                <Form.Check
                  type="checkbox"
                  checked={selectedList.some((s) => s.id === o.id)}
                  onChange={() => handleMultiToggle(o.id)}
                  label={o.label}
                />
              ) : (
                <span
                  title={o.label}
                  className="text-truncate"
                  style={{
                    maxWidth: '100%',
                    display: 'inline-block',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {selected && selected.id === o.id && '✓ '}
                  {o.label}
                </span>
              )}
            </Dropdown.Item>
          ))
        ) : (
          <Dropdown.Item disabled>No results</Dropdown.Item>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
}
